/**
 * Widget: Your Rank Badge
 * Shows user's rank compared to team
 */

import React from 'react';
import { Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { journeyTracker } from '../../lib/journeyTracker';
import { mockTeamMembers } from '../../data/mockTeamData';

interface YourRankBadgeProps {
  metric?: 'exploration' | 'achievements' | 'outcomes';
  compact?: boolean;
}

export const YourRankBadge: React.FC<YourRankBadgeProps> = ({
  metric = 'exploration',
  compact = false,
}) => {
  const journey = journeyTracker.getJourney();
  
  // Calculate user's score based on metric
  const userScore = metric === 'exploration'
    ? ((journey as any).visitedPages?.length || 0)
    : metric === 'achievements'
    ? ((journey as any).achievements?.length || 0)
    : 0; // outcomes would come from outcomeTracker

  // Simulate team scores (in real app, from backend)
  const teamScores = mockTeamMembers.map((member) => {
    const baseScore = member.frameworksExplored;
    const randomFactor = Math.floor(Math.random() * 5);
    return {
      name: member.name,
      score: baseScore + randomFactor,
    };
  });

  // Add user to the list
  const allScores = [
    ...teamScores,
    { name: 'You', score: userScore }
  ].sort((a, b) => b.score - a.score);

  const userRank = allScores.findIndex(s => s.name === 'You') + 1;
  const totalParticipants = allScores.length;
  const percentile = Math.round((1 - (userRank - 1) / totalParticipants) * 100);

  // Determine rank tier
  const tier = userRank <= 3 ? 'top'
    : userRank <= totalParticipants * 0.25 ? 'high'
    : userRank <= totalParticipants * 0.5 ? 'mid'
    : 'rising';

  const tierConfig = {
    top: {
      bg: 'bg-gradient-to-r from-amber-50 to-amber-100',
      border: 'border-amber-300',
      text: 'text-amber-700',
      icon: Trophy,
      label: '🏆 Top Performer',
    },
    high: {
      bg: 'bg-gradient-to-r from-primary-50 to-primary-100',
      border: 'border-primary-300',
      text: 'text-primary-700',
      icon: TrendingUp,
      label: '⭐ High Achiever',
    },
    mid: {
      bg: 'bg-gradient-to-r from-sage-50 to-sage-100',
      border: 'border-sage-300',
      text: 'text-sage-700',
      icon: Minus,
      label: '📈 On Track',
    },
    rising: {
      bg: 'bg-gradient-to-r from-charcoal-50 to-charcoal-100',
      border: 'border-charcoal-300',
      text: 'text-charcoal-700',
      icon: TrendingDown,
      label: '🌱 Rising Star',
    },
  };

  const config = tierConfig[tier];
  const Icon = config.icon;

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 ${config.bg} ${config.border} border rounded-full`}>
        <Icon className={`h-4 w-4 ${config.text}`} />
        <span className={`text-xs font-bold ${config.text}`}>
          #{userRank} of {totalParticipants}
        </span>
      </div>
    );
  }

  return (
    <div className={`${config.bg} ${config.border} border-2 rounded-lg p-4`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 ${config.text.replace('text-', 'bg-').replace('-700', '-600')} rounded-lg`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className={`text-sm font-bold ${config.text}`}>{config.label}</div>
          <div className="text-xs text-charcoal-600">Your team rank</div>
        </div>
      </div>
      
      <div className="flex items-baseline gap-3 mb-2">
        <div className={`text-3xl font-bold ${config.text}`}>
          #{userRank}
        </div>
        <div className="text-sm text-charcoal-600">
          of {totalParticipants} members
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-2 bg-white/50 rounded-full overflow-hidden">
          <div
            className={`h-full ${config.text.replace('text-', 'bg-')}`}
            style={{ width: `${percentile}%` }}
          />
        </div>
        <span className={`text-xs font-bold ${config.text}`}>
          Top {100 - percentile}%
        </span>
      </div>

      <div className="text-xs text-charcoal-600">
        {metric === 'exploration' && `${userScore} frameworks explored`}
        {metric === 'achievements' && `${userScore} achievements unlocked`}
        {metric === 'outcomes' && `${userScore} outcomes tracked`}
      </div>
    </div>
  );
};

